import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Save, ClipboardList, Pencil, Trash2, Search, X } from "lucide-react";

const REPORT_OPTIONS = [
  { value: "completo", label: "Reportou (+5 pts)" },
  { value: "nao_reportou", label: "Não Reportou (-10 pts)" },
];

// Normaliza para pesquisa acento- e case-insensível
const norm = (s) => (s || "")
  .toString()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

// Combobox com pesquisa por nome. Lista ordenada A→Z, mostra equipa/MBTI ao lado.
// Permite escrever para filtrar (~240 consultores) sem perder a precisão do click.
function PlayerCombobox({ players, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const sorted = useMemo(
    () => [...players].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "pt", { sensitivity: "base" }),
    ),
    [players],
  );

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return sorted;
    return sorted.filter((p) => {
      const hay = [p.name, p.email, p.team_name, p.club, p.mbti].map(norm).join(" ");
      return hay.includes(q);
    });
  }, [sorted, query]);

  // Quando seleção externa muda (ex: clicar em Editar), espelhar no input
  const selected = sorted.find((p) => p.id === value);
  useEffect(() => {
    setQuery(selected ? selected.name : "");
  }, [selected]);

  const pick = (p) => {
    onChange(p.id);
    setQuery(p.name);
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(true);
  };

  return (
    <div className="relative" data-testid="player-combobox">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
        <input
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder="Escrever nome para procurar…"
          className="kw-input pl-9 pr-9"
          data-testid="form-player"
          autoComplete="off"
        />
        {query && (
          <button type="button" onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white"
            data-testid="form-player-clear"
            aria-label="Limpar">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-[#0A0A0A] border border-[#333] shadow-lg"
          data-testid="form-player-options"
        >
          {filtered.map((p) => {
            const isAssistant = p.role_in_team === "assistant";
            const isTL = p.is_team_lead;
            return (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()} // evita perder focus antes do click
                onClick={() => pick(p)}
                className={`w-full text-left px-3 py-2 hover:bg-[#1A1A1A] flex items-center justify-between gap-3 border-b border-[#1A1A1A] ${
                  p.id === value ? "bg-[#1A1A1A]" : ""
                }`}
                data-testid={`form-player-option-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{p.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-0.5">
                    {p.club || "—"} {p.mbti && <>· {p.mbti}</>} {p.team_name && <>· {p.team_name}</>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {isTL && <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold border border-[#E50000] text-[#E50000]">TL</span>}
                  {isAssistant && <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold border border-[#FFB020] text-[#FFB020]">Ass.</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {open && filtered.length === 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#333] px-3 py-3 text-xs text-[#737373]">
          Nenhum consultor encontrado.
        </div>
      )}
    </div>
  );
}

const initial = {
  player_id: "",
  week_number: 1,
  contactos: 0,
  reunioes: 0,
  angariacoes: 0,
  vendas: 0,
  social_posts: 0,
  humanized_videos: 0,
  formacoes: 0,
  social_post_url: "",
  humanized_video_url: "",
  growth_share_count: 0,
  growth_share_internal: 0,
  presente: true,
  pontual: true,
  reporting: "completo",
  tpc: true,
  notes: "",
};

// Mesmo critério do ranking: só semanas de Coopetição e a partir da semana em que a pessoa entrou.
function countsForRanking(e, player, s) {
  const prep = Number(s.prep_weeks || 0);
  const total = Number(s.weeks_total || 10);
  const comp = Number(s.competition_weeks) || Math.max(1, total - prep - Number(s.celebration_weeks || 0));
  const w = e.week_number || 0;
  if (w < prep + 1 || w > prep + comp) return false;
  if (player && player.league_status && player.league_status !== "competing") return false;
  return !(player?.competing_since_week && w < player.competing_since_week);
}


export default function WeeklyEntry() {
  const [players, setPlayers] = useState([]);
  const [form, setForm] = useState(initial);
  const [confirmedTrainings, setConfirmedTrainings] = useState(0);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [settings, setSettings] = useState({ current_week: 1, weeks_total: 10 });
  const [weekRange, setWeekRange] = useState(null); // { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' }

  const refresh = useCallback(() => api.get("/entries").then((r) => setRecent(r.data.slice(-20).reverse())), []);

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setForm((f) => ({ ...f, week_number: r.data.current_week_computed || r.data.current_week || 1 }));
    });
    refresh();
  }, [refresh]);

  // Auto-load existing entry when (player_id, week_number) changes.
  // If no entry exists for that pair, reset numeric/toggle fields so admin can create fresh.
  useEffect(() => {
    if (!form.player_id || !form.week_number) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/entries?week=${form.week_number}&player_id=${form.player_id}`);
        if (cancelled) return;
        const existing = (r.data || []).find(
          (e) => e.player_id === form.player_id && e.week_number === form.week_number
        );
        setConfirmedTrainings((existing?.trainings_confirmed || []).length);
        if (existing) {
          setForm((f) => ({
            ...f,
            contactos: existing.contactos || 0,
            reunioes: existing.reunioes || 0,
            angariacoes: existing.angariacoes || 0,
            vendas: existing.vendas || 0,
            social_posts: existing.social_posts || 0,
            humanized_videos: existing.humanized_videos || 0,
            formacoes: existing.formacoes || 0,
            social_post_url: existing.social_post_url || "",
            humanized_video_url: existing.humanized_video_url || "",
            growth_share_count: existing.growth_share_count || 0,
            growth_share_internal: existing.growth_share_internal || 0,
            presente: existing.presente !== false,
            pontual: existing.pontual !== false,
            reporting: existing.reporting || "completo",
            tpc: existing.tpc !== false,
            notes: existing.notes || "",
          }));
        } else {
          // Fresh (player, week) — clear counters, keep player+week
          setForm((f) => ({
            ...initial,
            player_id: f.player_id,
            week_number: f.week_number,
          }));
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [form.player_id, form.week_number]);

  // Compute the Monday→Sunday date range for the selected week.
  useEffect(() => {
    if (!settings.start_date || !form.week_number) { setWeekRange(null); return; }
    const start = new Date(settings.start_date + "T00:00:00");
    // Snap to Monday of the same calendar week (matches backend _parse_start_date logic)
    const dow = start.getDay(); // 0=Sun..6=Sat
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    start.setDate(start.getDate() + mondayOffset);
    // Move to the requested week
    start.setDate(start.getDate() + (form.week_number - 1) * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fmt = (d) => d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" });
    setWeekRange({ start: fmt(start), end: fmt(end) });
  }, [settings.start_date, form.week_number]);

  const loadIntoForm = (e) => {
    setForm({
      player_id: e.player_id,
      week_number: e.week_number,
      contactos: e.contactos,
      reunioes: e.reunioes,
      angariacoes: e.angariacoes,
      vendas: e.vendas,
      social_posts: e.social_posts || 0,
      humanized_videos: e.humanized_videos || 0,
      formacoes: e.formacoes || 0,
      social_post_url: e.social_post_url || "",
      humanized_video_url: e.humanized_video_url || "",
      growth_share_count: e.growth_share_count,
      growth_share_internal: e.growth_share_internal || 0,
      presente: e.presente,
      pontual: e.pontual,
      reporting: e.reporting,
      tpc: e.tpc,
      notes: e.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.info("Registo carregado. Edita e guarda para atualizar.");
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Eliminar este registo?")) return;
    try { await api.delete(`/entries/${id}`); toast.success("Eliminado"); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.player_id) return toast.error("Escolhe um consultor");
    setSaving(true);
    try {
      await api.post("/entries", form);
      toast.success("Registo guardado");
      setForm({ ...initial, week_number: form.week_number, player_id: form.player_id });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="weekly-entry-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kw-overline mb-2">Operações</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Registo <span className="text-[#E50000]">Semanal</span>
          </h1>
        </div>
        <div className="text-sm text-[#A3A3A3]">
          Semana atual: <span className="text-white font-display text-xl font-black">{settings.current_week_computed || settings.current_week}</span>/{settings.weeks_total}
        </div>
      </div>

      <form onSubmit={submit} className="kw-card p-6 grid md:grid-cols-3 gap-4" data-testid="weekly-form">
        <Field label="Consultor / Assistente">
          <PlayerCombobox
            players={players}
            value={form.player_id}
            onChange={(id) => set("player_id", id)}
          />
        </Field>

        <Field label="Semana">
          <input type="number" min="1" className="kw-input" value={form.week_number}
            onChange={(e) => set("week_number", Number(e.target.value))} data-testid="form-week" />
          {weekRange && (
            <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1" data-testid="week-range">
              Seg {weekRange.start} → Dom {weekRange.end}
            </div>
          )}
        </Field>

        <Field label="Reporting">
          <select className="kw-input" value={form.reporting} onChange={(e) => set("reporting", e.target.value)} data-testid="form-reporting">
            {REPORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>

        <NumberField label="Contactos (+1 cada)" value={form.contactos} onChange={(v) => set("contactos", v)} testid="form-contactos" />
        <NumberField label="Reuniões (+15 cada)" value={form.reunioes} onChange={(v) => set("reunioes", v)} testid="form-reunioes" />
        <NumberField label="Angariações (+20 cada)" value={form.angariacoes} onChange={(v) => set("angariacoes", v)} testid="form-angariacoes" />
        <NumberField label="Vendas (+50 cada)" value={form.vendas} onChange={(v) => set("vendas", v)} testid="form-vendas" />
        <NumberField label="Posts redes sociais (+5 cada)" value={form.social_posts} onChange={(v) => set("social_posts", v)} testid="form-social-posts" />
        <NumberField label="Vídeos humanizados (+10 cada)" value={form.humanized_videos} onChange={(v) => set("humanized_videos", v)} testid="form-humanized-videos" />
        {confirmedTrainings > 0 ? (
          <div className="border border-[#EAB308]/50 bg-[#EAB308]/5 p-3" data-testid="form-formacoes-locked">
            <div className="kw-overline text-[#EAB308]">Formações (+8 cada)</div>
            <div className="font-display text-2xl font-black">{form.formacoes}</div>
            <p className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-1">
              confirmadas pelo formador na chamada de presenças — não precisas de as registar
            </p>
          </div>
        ) : (
          <NumberField label="Formações (+8 cada)" value={form.formacoes} onChange={(v) => set("formacoes", v)} testid="form-formacoes" />
        )}
        <NumberField label="GS externo · KW Lead (+100 cada)" value={form.growth_share_count} onChange={(v) => set("growth_share_count", v)} testid="form-gs-ext" />
        <NumberField label="GS interno · KW → Jogo (+20 cada)" value={form.growth_share_internal} onChange={(v) => set("growth_share_internal", v)} testid="form-gs-int" />

        <Field label="Presente?">
          <ToggleRow value={form.presente} onChange={(v) => set("presente", v)} testid="form-presente" />
        </Field>
        <Field label="Pontual? (+5)">
          <ToggleRow value={form.pontual} onChange={(v) => set("pontual", v)} testid="form-pontual" />
        </Field>
        <Field label="TPC realizado? (+5)">
          <ToggleRow value={form.tpc} onChange={(v) => set("tpc", v)} testid="form-tpc" />
        </Field>

        <div className="md:col-span-3 grid md:grid-cols-2 gap-4">
          <Field label="URL post redes sociais (opcional)">
            <input type="url" className="kw-input" value={form.social_post_url}
              placeholder="https://linkedin.com/posts/..."
              onChange={(e) => set("social_post_url", e.target.value)} data-testid="form-social-post-url" />
          </Field>
          <Field label="URL vídeo humanizado (opcional)">
            <input type="url" className="kw-input" value={form.humanized_video_url}
              placeholder="https://youtu.be/..."
              onChange={(e) => set("humanized_video_url", e.target.value)} data-testid="form-humanized-video-url" />
          </Field>
        </div>

        <div className="md:col-span-3">
          <Field label="Notas">
            <textarea className="kw-input min-h-[60px]" value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="form-notes" />
          </Field>
        </div>

        <div className="md:col-span-3 flex justify-end pt-2">
          <button className="kw-btn-primary inline-flex items-center gap-2" disabled={saving} data-testid="form-submit">
            <Save className="w-4 h-4" /> {saving ? "A guardar…" : "Guardar registo"}
          </button>
        </div>
      </form>

      <section>
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="w-5 h-5 text-[#E50000]" />
          <h2 className="font-display text-2xl font-black uppercase">Registos recentes</h2>
        </div>
        <div className="kw-card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-[#0A0A0A] border-b border-[#222]">
              <tr className="kw-overline">
                <th className="p-3">Semana</th>
                <th className="p-3">Consultor</th>
                <th className="p-3 text-right">C</th>
                <th className="p-3 text-right">R</th>
                <th className="p-3 text-right">A</th>
                <th className="p-3 text-right">V</th>
                <th className="p-3 text-right">GS</th>
                <th className="p-3">Report.</th>
                <th className="p-3 text-right">Pontos</th>
                <th className="p-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && <tr><td colSpan="10" className="p-6 text-center text-[#737373]">Sem registos</td></tr>}
              {recent.map((e) => {
                const player = players.find((p) => p.id === e.player_id);
                return (
                  <tr key={e.id} className="border-b border-[#1A1A1A] hover:bg-[#141414]">
                    <td className="p-3">{e.week_number}</td>
                    <td className="p-3">{player?.name || e.player_id}</td>
                    <td className="p-3 text-right">{e.contactos}</td>
                    <td className="p-3 text-right">{e.reunioes}</td>
                    <td className="p-3 text-right">{e.angariacoes}</td>
                    <td className="p-3 text-right">{e.vendas}</td>
                    <td className="p-3 text-right">{e.growth_share_count}</td>
                    <td className="p-3 text-xs">{e.reporting}</td>
                    <td className="p-3 text-right font-display font-black">
                      {Math.round(e.points)}
                      {!countsForRanking(e, player, settings) && (
                        <div className="font-sans font-normal text-[9px] uppercase tracking-widest text-[#EAB308]" data-testid={`entry-not-ranked-${e.id}`}>
                          não conta p/ ranking
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button onClick={() => loadIntoForm(e)} className="text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold mr-3" data-testid={`edit-entry-${e.id}`}>
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                      <button onClick={() => deleteEntry(e.id)} className="text-[#FF6B6B] hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold" data-testid={`del-entry-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="kw-overline block mb-2">{label}</label>
      {children}
    </div>
  );
}

function NumberField({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <input type="number" min="0" className="kw-input" value={value}
        onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} />
    </Field>
  );
}

function ToggleRow({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      <button type="button" onClick={() => onChange(true)}
        className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest border ${value ? "bg-[#E50000] border-[#E50000] text-white" : "bg-transparent border-[#333] text-[#A3A3A3] hover:text-white"}`}>
        Sim
      </button>
      <button type="button" onClick={() => onChange(false)}
        className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest border ${!value ? "bg-[#1A1A1A] border-white text-white" : "bg-transparent border-[#333] text-[#A3A3A3] hover:text-white"}`}>
        Não
      </button>
    </div>
  );
}
